/**
 * 实时通知铃铛 - 顶部 NavBar 右侧
 * - 30s 轮询 unread 计数
 * - 红点 badge 显示未读数
 * - 下拉显示最近 5 条
 * - 新通知到达时 toast 弹出
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Dropdown, Badge, notification, Empty } from 'antd';
import { BellOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import dayjs from 'dayjs';

const GOLD = '#B8892A';
const BD = '#E8E0D0';
const POLL_INTERVAL_MS = 30_000; // 30 秒轮询

const TYPE_LABEL_KEYS = {
  billing: 'notificationBell.billing',
  bid_won: 'notificationBell.bidWon',
  bid_lost: 'notificationBell.bidLost',
  inspection_done: 'notificationBell.inspectionDone',
  shipped: 'notificationBell.shipped',
  system: 'notificationBell.system',
};

// 点击通知预览统一跳转到消息中心，并定位到这条消息

export default function NotificationBell() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isLoggedIn, user } = useAuth();
  const [unread, setUnread] = useState(0);
  const [recent, setRecent] = useState([]);
  const [open, setOpen] = useState(false);
  const lastUnreadRef = useRef(0);   // 上一轮的 unread 数（用于检测新增）
  const isFirstFetch = useRef(true); // 首次拉取不弹 toast

  // silent=true: 由 listener 触发的刷新，不再广播 event，避免双向回音
  const fetchNotifications = useCallback(async (silent = false) => {
    if (!isLoggedIn || user?.type === 'staff') return;
    try {
      const { data } = await api.get('/notifications', { params: { limit: 5 } });
      const list = data.data || [];
      const newUnread = data.unread || 0;
      const prevUnread = lastUnreadRef.current;
      // 检测新通知到达 → 弹 toast
      if (!isFirstFetch.current && newUnread > prevUnread) {
        const diff = newUnread - prevUnread;
        const latestUnread = list.find(n => !n.is_read);
        notification.info({
          message: t('notificationBell.newMessages', { count: diff }),
          description: latestUnread ? latestUnread.title : t('notificationBell.clickToView'),
          duration: 5,
          placement: 'topRight',
          icon: <BellOutlined style={{ color: GOLD }} />,
          onClick: () => { setOpen(true); },
          style: { cursor: 'pointer' },
        });
      }
      const wasFirstFetch = isFirstFetch.current;
      isFirstFetch.current = false;
      lastUnreadRef.current = newUnread;
      setUnread(newUnread);
      setRecent(list);
      // 仅在「数量发生变化」时广播；silent 模式（被 event listener 调起）不广播
      if (!silent && !wasFirstFetch && newUnread !== prevUnread) {
        window.dispatchEvent(new Event('notifications:updated'));
      }
    } catch (_) { /* silently */ }
  }, [isLoggedIn, user]);

  useEffect(() => {
    if (!isLoggedIn || user?.type === 'staff') return;
    fetchNotifications();
    const t = setInterval(fetchNotifications, POLL_INTERVAL_MS);
    return () => clearInterval(t);
  }, [fetchNotifications, isLoggedIn, user]);

  // 监听消息中心页面的用户操作（标为已读/全部已读/删除）→ 刷新铃铛
  useEffect(() => {
    if (!isLoggedIn || user?.type === 'staff') return;
    const handler = () => fetchNotifications(true); // silent: 不再广播，避免回音
    window.addEventListener('notifications:updated', handler);
    return () => window.removeEventListener('notifications:updated', handler);
  }, [fetchNotifications, isLoggedIn, user]);

  const handleClickItem = async (notif) => {
    setOpen(false);
    // 立即在 UI 标记已读，避免视觉延迟
    if (!notif.is_read) {
      setRecent(prev => prev.map(n => n.id === notif.id ? { ...n, is_read: true } : n));
      setUnread(u => Math.max(0, u - 1));
      lastUnreadRef.current = Math.max(0, lastUnreadRef.current - 1);
      api.patch(`/notifications/${notif.id}/read`).catch(() => {});
      // 通知消息中心页面：用户在铃铛预览里标了已读
      window.dispatchEvent(new Event('notifications:updated'));
    }
    // 跳到消息中心并自动选中这条
    navigate('/notifications', { state: { focusId: notif.id } });
  };

  const handleViewAll = () => {
    setOpen(false);
    navigate('/notifications');
  };

  const handleMarkAllRead = async () => {
    try {
      await api.patch('/notifications/read-all');
      setRecent(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnread(0);
      lastUnreadRef.current = 0;
      window.dispatchEvent(new Event('notifications:updated'));
    } catch (_) {}
  };

  if (!isLoggedIn || user?.type === 'staff') return null;

  const dropdownContent = (
    <div style={{
      width: 360,
      background: '#fff',
      border: `1px solid ${BD}`,
      borderRadius: 8,
      boxShadow: '0 6px 24px rgba(0,0,0,.10)',
      overflow: 'hidden',
    }}>
      <div style={{
        padding: '10px 14px',
        borderBottom: `1px solid ${BD}`,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        background: '#FAFAF8',
      }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: '#110E0A' }}>
          {t('notifications.title')} {unread > 0 && <span style={{ color: GOLD, marginLeft: 6 }}>· {t('notificationBell.unreadCount', { count: unread })}</span>}
        </span>
        {unread > 0 && (
          <span
            onClick={handleMarkAllRead}
            style={{ fontSize: 11, color: '#6B5E4E', cursor: 'pointer', textDecoration: 'underline' }}
          >
            {t('notifications.markAllRead')}
          </span>
        )}
      </div>
      <div style={{ maxHeight: 380, overflowY: 'auto' }}>
        {recent.length === 0 ? (
          <div style={{ padding: 30 }}>
            <Empty description={t('notifications.empty')} image={Empty.PRESENTED_IMAGE_SIMPLE} />
          </div>
        ) : recent.map(n => (
          <div
            key={n.id}
            onClick={() => handleClickItem(n)}
            style={{
              padding: '10px 14px',
              borderBottom: `1px solid ${BD}`,
              cursor: 'pointer',
              background: n.is_read ? '#fff' : 'rgba(184,137,42,.05)',
              transition: 'background .12s',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = '#FAFAF8'; }}
            onMouseLeave={e => { e.currentTarget.style.background = n.is_read ? '#fff' : 'rgba(184,137,42,.05)'; }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 3 }}>
              <span style={{ fontSize: 12, color: GOLD, fontWeight: 500 }}>
                {!n.is_read && <span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', background: '#E63946', marginRight: 6 }} />}
                {TYPE_LABEL_KEYS[n.type] ? t(TYPE_LABEL_KEYS[n.type]) : n.type}
              </span>
              <span style={{ fontSize: 10, color: '#A8998A' }}>
                {n.created_at ? dayjs(n.created_at).format('MM-DD HH:mm') : ''}
              </span>
            </div>
            <div style={{ fontSize: 13, fontWeight: 500, color: '#110E0A', marginBottom: 3, lineHeight: 1.4 }}>
              {n.title}
            </div>
            <div style={{ fontSize: 12, color: '#6B5E4E', lineHeight: 1.45, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitBoxOrient: 'vertical', WebkitLineClamp: 2 }}>
              {n.body}
            </div>
          </div>
        ))}
      </div>
      <div
        onClick={handleViewAll}
        style={{
          padding: '10px 14px',
          textAlign: 'center',
          cursor: 'pointer',
          fontSize: 12,
          color: GOLD,
          background: '#FAFAF8',
          borderTop: `1px solid ${BD}`,
        }}
      >
        {t('notificationBell.viewAll')} →
      </div>
    </div>
  );

  return (
    <Dropdown
      open={open}
      onOpenChange={setOpen}
      dropdownRender={() => dropdownContent}
      placement="bottomRight"
      trigger={['click']}
    >
      <Badge count={unread} size="small" offset={[-2, 4]}>
        <button
          style={{
            width: 36,
            height: 36,
            border: `1px solid ${BD}`,
            background: '#fff',
            borderRadius: '50%',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.15s',
          }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = GOLD; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = BD; }}
          aria-label={t('notifications.title')}
        >
          <BellOutlined style={{ fontSize: 16, color: unread > 0 ? GOLD : '#6B5E4E' }} />
        </button>
      </Badge>
    </Dropdown>
  );
}

